import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authApi, usersApi } from '../services/api';
import { useAuthStore } from '../stores/auth.store';
import { websocket } from '../services/websocket';
import { useToast } from '../contexts/ToastContext';
import type { LoginInput, SignupInput } from '../types';

export function useAuth() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isAuthenticated, login, logout, setUser } = useAuthStore();
  const toast = useToast();

  const loginMutation = useMutation({
    mutationFn: (input: LoginInput) => authApi.login(input),
    onSuccess: (data) => {
      login(data.user); // tokens are set by the server as httpOnly cookies
      websocket.connect();
      queryClient.invalidateQueries();
      toast.success('Signed in successfully!');
      navigate('/discover');
    },
    onError: () => {
      toast.error('Invalid credentials. Please try again.');
    },
  });

  const signupMutation = useMutation({
    mutationFn: (input: SignupInput) => authApi.signup(input),
    onSuccess: (data) => {
      login(data.user); // tokens are set by the server as httpOnly cookies
      websocket.connect();
      toast.success('Account created successfully!');
      navigate('/profile/create');
    },
    onError: () => {
      toast.error('Could not create account. Please try again.');
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      websocket.disconnect();
      logout();
      queryClient.clear();
      navigate('/login');
    },
    onError: () => {
      websocket.disconnect();
      logout();
      queryClient.clear();
      navigate('/login');
    },
  });

  const { data: userData, refetch: refreshUser } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: usersApi.getMe,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  // Update user in store when query data changes (React Query v5 pattern)
  useEffect(() => {
    if (userData) {
      setUser(userData);
    }
  }, [userData, setUser]);

  return {
    user,
    isAuthenticated,
    login: loginMutation.mutate,
    signup: signupMutation.mutate,
    logout: logoutMutation.mutate,
    refreshUser,
    isLoggingIn: loginMutation.isPending,
    isSigningUp: signupMutation.isPending,
    loginError: loginMutation.error,
    signupError: signupMutation.error,
  };
}
